import { ref, watch } from 'vue'
import type { Ref } from 'vue'
import { useStatsStore } from '@/stores/stats-store'
import { useRegionStore } from '@/stores/region-store'
import { getApiV1LotteryYears, getApiV1LotterySearch49208 } from '@/api/sdk.gen'
import type { LotteryRecord } from '@/api/types.gen'
import { getBallColor, getZodiac, getZodiacYear, parseLotteryDate } from '@/utils/lottery-attr'
import type { BallColor } from '@/components/lottery-ball-row'

// ==================== 类型 ====================

export interface NumberStatItem {
  number: number
  color: BallColor
  count: number
}

export interface StringStatItem {
  label: string
  count: number
}

// ==================== 数据获取 ====================

export async function fetchYears(region: 'hk' | 'aocai'): Promise<number[]> {
  const res = await getApiV1LotteryYears({ query: { region } })
  return (res.data?.data ?? []).sort((a, b) => b - a)
}

export async function fetchRecordsByYear(
  region: 'hk' | 'aocai',
  year: number,
): Promise<LotteryRecord[]> {
  const collected: LotteryRecord[] = []
  let page = 1
  while (true) {
    const res = await getApiV1LotterySearch49208({
      query: { region, year, pageNum: page, pageSize: 100, sort: 2 },
    })
    const records = res.data?.data?.recordList ?? []
    if (records.length === 0) break
    collected.push(...records)
    if (records.length < 100) break
    page++
  }
  return collected
}

export async function fetchRecentRecords(
  region: 'hk' | 'aocai',
  need: number,
): Promise<LotteryRecord[]> {
  // 获取可用年份（降序）
  const yearsRes = await getApiV1LotteryYears({ query: { region } })
  const years = (yearsRes.data?.data ?? []).sort((a, b) => b - a)
  if (years.length === 0) return []

  const collected: LotteryRecord[] = []

  for (const year of years) {
    if (collected.length >= need) break
    let page = 1
    while (collected.length < need) {
      const res = await getApiV1LotterySearch49208({
        query: { region, year, pageNum: page, pageSize: 100, sort: 2 },
      })
      const records = res.data?.data?.recordList ?? []
      if (records.length === 0) break
      collected.push(...records)
      if (records.length < 100) break
      page++
    }
  }

  return collected.slice(0, need)
}

// ==================== 统计计算 ====================

/**
 * 每个 key 的出现次数 freq，以及从最新一期往回数第一次出现的位置 missing（没出现过记为总期数）。
 * extract 返回一期里出现的 key；同一期返回多次就计多次。
 */
export function countHits<K>(records: LotteryRecord[], extract: (r: LotteryRecord) => Iterable<K>) {
  const freq = new Map<K, number>()
  const firstSeen = new Map<K, number>()
  records.forEach((r, i) => {
    for (const k of extract(r)) {
      freq.set(k, (freq.get(k) ?? 0) + 1)
      if (!firstSeen.has(k)) firstSeen.set(k, i)
    }
  })
  return {
    freq: (k: K) => freq.get(k) ?? 0,
    missing: (k: K) => firstSeen.get(k) ?? records.length,
  }
}


/** 提取每期的特码号码 */
export function extractSpecialNum(r: LotteryRecord): number[] {
  const item = r.numberList?.[6]
  if (!item?.number) return []
  return [Number.parseInt(item.number)]
}

/** 提取每期的正码号码（前6个） */
export function extractRegularNums(r: LotteryRecord): number[] {
  return (r.numberList ?? [])
    .slice(0, 6)
    .map((n) => Number.parseInt(n.number ?? '0'))
    .filter((n) => n > 0)
}

/** 根据开奖时间解析生肖年份(用于号码 → 生肖的反查) */
function getZodiacYearFromRecord(r: LotteryRecord): number | null {
  if (!r.lotteryTime) return null
  return getZodiacYear(parseLotteryDate(r.lotteryTime))
}

/** 提取特码生肖(通过号码 + lotteryTime 推算,与 lottery-attr.getZodiac 一致) */
export function extractSpecialZodiac(r: LotteryRecord): string[] {
  const item = r.numberList?.[6]
  const num = Number.parseInt(item?.number ?? '0')
  if (num <= 0) return []
  const zy = getZodiacYearFromRecord(r)
  if (zy == null) return []
  return [getZodiac(zy, num)]
}

/** 提取正码生肖(6 个正码各推算) */
export function extractRegularZodiacs(r: LotteryRecord): string[] {
  const zy = getZodiacYearFromRecord(r)
  if (zy == null) return []
  return (r.numberList ?? [])
    .slice(0, 6)
    .map((n) => Number.parseInt(n.number ?? '0'))
    .filter((num) => num > 0)
    .map((num) => getZodiac(zy, num))
}

/** 提取特码波色 */
export function extractSpecialColor(r: LotteryRecord): string[] {
  const item = r.numberList?.[6]
  if (!item?.number) return []
  const num = Number.parseInt(item.number)
  const c = getBallColor(num)
  return [colorLabel(c)]
}

/** 提取正码波色 */
export function extractRegularColors(r: LotteryRecord): string[] {
  return (r.numberList ?? [])
    .slice(0, 6)
    .map((n) => {
      const num = Number.parseInt(n.number ?? '0')
      return num > 0 ? colorLabel(getBallColor(num)) : ''
    })
    .filter(Boolean)
}

/** 提取特码尾数 */
export function extractSpecialTail(r: LotteryRecord): string[] {
  const item = r.numberList?.[6]
  if (!item?.number) return []
  const num = Number.parseInt(item.number)
  return [`${num % 10}尾`]
}

/** 提取正码尾数(6 个正码各取尾数) */
export function extractRegularTail(r: LotteryRecord): string[] {
  return (r.numberList ?? [])
    .slice(0, 6)
    .map((n) => {
      const num = Number.parseInt(n.number ?? '0')
      return num > 0 ? `${num % 10}尾` : ''
    })
    .filter(Boolean)
}

function colorLabel(c: BallColor): string {
  if (c === 'red') return '红波'
  if (c === 'blue') return '蓝波'
  if (c === 'green') return '绿波'
  return ''
}

/** 号码频率统计 → 取 Top N */
function calcNumberFrequency(
  records: LotteryRecord[],
  extractor: (r: LotteryRecord) => number[],
  topN: number,
): NumberStatItem[] {
  const freq = new Map<number, number>()
  for (const r of records) {
    for (const num of extractor(r)) {
      freq.set(num, (freq.get(num) ?? 0) + 1)
    }
  }
  return [...freq.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([num, count]) => ({ number: num, color: getBallColor(num), count }))
}

/** 号码遗漏统计 → 取 Top N */
function calcNumberMissing(
  records: LotteryRecord[],
  extractor: (r: LotteryRecord) => number[],
  topN: number,
): NumberStatItem[] {
  // 记录每个号码首次出现的索引（从最新期开始）
  const firstSeen = new Map<number, number>()
  for (let i = 0; i < records.length; i++) {
    for (const num of extractor(records[i]!)) {
      if (!firstSeen.has(num)) firstSeen.set(num, i)
    }
  }
  // 1-49 中未出现的号码遗漏值为 records.length
  const result: NumberStatItem[] = []
  for (let num = 1; num <= 49; num++) {
    const missing = firstSeen.has(num) ? firstSeen.get(num)! : records.length
    result.push({ number: num, color: getBallColor(num), count: missing })
  }
  result.sort((a, b) => b.count - a.count)
  return result.slice(0, topN)
}

/** 字符串维度频率统计 → 取 Top N */
function calcStringFrequency(
  records: LotteryRecord[],
  extractor: (r: LotteryRecord) => string[],
  topN: number,
): StringStatItem[] {
  const freq = new Map<string, number>()
  for (const r of records) {
    for (const val of extractor(r)) {
      freq.set(val, (freq.get(val) ?? 0) + 1)
    }
  }
  return [...freq.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([label, count]) => ({ label, count }))
}

/** 字符串维度遗漏统计 → 取 Top N */
function calcStringMissing(
  records: LotteryRecord[],
  extractor: (r: LotteryRecord) => string[],
  topN: number,
): StringStatItem[] {
  const firstSeen = new Map<string, number>()
  for (let i = 0; i < records.length; i++) {
    for (const val of extractor(records[i]!)) {
      if (!firstSeen.has(val)) firstSeen.set(val, i)
    }
  }
  // 所有出现过的 key + 未出现的遗漏值为 records.length
  const allKeys = new Set(firstSeen.keys())
  const result: StringStatItem[] = [...allKeys].map((label) => ({
    label,
    count: firstSeen.get(label)!,
  }))
  result.sort((a, b) => b.count - a.count)
  return result.slice(0, topN)
}

// ==================== Composable ====================

/**
 * 取「当前统计期数」对应的最近 N 期开奖并用 compute 统计。
 * 统计期数或彩种变化时 300ms 防抖重算；创建时立即算一次。
 */
export function useRecentStats<T>(compute: (records: LotteryRecord[]) => T, initial: T) {
  const statsStore = useStatsStore()
  const regionStore = useRegionStore()

  const loading = ref(false)
  const stats = ref(initial) as Ref<T>

  async function refresh() {
    if (statsStore.periodCount == null) return
    loading.value = true
    try {
      const records = await fetchRecentRecords(regionStore.region, statsStore.periodCount)
      stats.value = compute(records)
    } finally {
      loading.value = false
    }
  }

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  function debouncedRefresh() {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(refresh, 300)
  }

  watch(() => statsStore.periodCount, debouncedRefresh)
  watch(() => regionStore.region, debouncedRefresh)
  refresh()

  return { loading, stats }
}

export function useLottoStats() {
  const specialNumFreq = ref<NumberStatItem[]>([])
  const specialNumMissing = ref<NumberStatItem[]>([])
  const regularNumFreq = ref<NumberStatItem[]>([])
  const regularNumMissing = ref<NumberStatItem[]>([])
  const specialZodiacFreq = ref<StringStatItem[]>([])
  const specialZodiacMissing = ref<StringStatItem[]>([])
  const regularZodiacFreq = ref<StringStatItem[]>([])
  const regularZodiacMissing = ref<StringStatItem[]>([])
  const specialColorFreq = ref<StringStatItem[]>([])
  const specialColorMissing = ref<StringStatItem[]>([])
  const regularColorFreq = ref<StringStatItem[]>([])
  const regularColorMissing = ref<StringStatItem[]>([])
  const specialTailFreq = ref<StringStatItem[]>([])
  const specialTailMissing = ref<StringStatItem[]>([])

  // 一次取数，算出全部 14 组统计
  const { loading } = useRecentStats((records) => {
    specialNumFreq.value = calcNumberFrequency(records, extractSpecialNum, 10)
    specialNumMissing.value = calcNumberMissing(records, extractSpecialNum, 10)
    regularNumFreq.value = calcNumberFrequency(records, extractRegularNums, 10)
    regularNumMissing.value = calcNumberMissing(records, extractRegularNums, 5)

    specialZodiacFreq.value = calcStringFrequency(records, extractSpecialZodiac, 6)
    specialZodiacMissing.value = calcStringMissing(records, extractSpecialZodiac, 5)
    regularZodiacFreq.value = calcStringFrequency(records, extractRegularZodiacs, 5)
    regularZodiacMissing.value = calcStringMissing(records, extractRegularZodiacs, 5)

    specialColorFreq.value = calcStringFrequency(records, extractSpecialColor, 3)
    specialColorMissing.value = calcStringMissing(records, extractSpecialColor, 3)
    regularColorFreq.value = calcStringFrequency(records, extractRegularColors, 3)
    regularColorMissing.value = calcStringMissing(records, extractRegularColors, 3)

    specialTailFreq.value = calcStringFrequency(records, extractSpecialTail, 3)
    specialTailMissing.value = calcStringMissing(records, extractSpecialTail, 5)
  }, undefined)

  return {
    loading,
    specialNumFreq,
    specialNumMissing,
    regularNumFreq,
    regularNumMissing,
    specialZodiacFreq,
    specialZodiacMissing,
    regularZodiacFreq,
    regularZodiacMissing,
    specialColorFreq,
    specialColorMissing,
    regularColorFreq,
    regularColorMissing,
    specialTailFreq,
    specialTailMissing,
  }
}
